import {useTranslation} from '@/translations/i18n.tsx'
import {ModalBody, ModalFooter, ModalParagraph, ModalTitle} from '@components/ui/modal-atoms'
import {FC, useState} from 'react'
import {Spinner} from '@components/ui/spinner'
import {Button} from '@components/ui/button'
import 'react-phone-input-2/lib/bootstrap.css'
import OtpInput from 'react-otp-input'
import {maskPhoneNumber} from '@utilities/helpers'
import {useValidateVerificationPhoneCode} from '@features/settings/queries/useValidateVerificationPhoneCode'
import {useResendVerificationPhoneCode} from '@features/settings/queries/useResendVerificationPhoneCode'
import {toast} from '@utilities/toast'

interface OtpSmsPendingConfirmationStepProps {
    phoneNumberValue: string
    onClose: () => void
}

export const OtpSmsPendingConfirmationStep: FC<OtpSmsPendingConfirmationStepProps> = ({phoneNumberValue, onClose}) => {
    const {t} = useTranslation()
    const [otp, setOtp] = useState('')

    const requestVerificationPhoneCodeMutation = useValidateVerificationPhoneCode({
        onSuccess: onClose
    })
    const onSuccessResendVerificationPhoneCode = () => {
        toast.success(t('settings:personal_information:verification_code_sent'))
    }
    const resendVerificationPhoneCodeMutation = useResendVerificationPhoneCode({
        onSuccess: onSuccessResendVerificationPhoneCode
    })

    const handleSubmit = () => {
        if (otp.length === 4) {
            requestVerificationPhoneCodeMutation.mutate({
                payload: {verification_code: Number(otp)}
            })
        }
    }

    const isPending = requestVerificationPhoneCodeMutation.isPending || resendVerificationPhoneCodeMutation.isPending
    return (
        <>
            <ModalBody>
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                        <ModalTitle>{t('settings:personal_information:enter_code')}</ModalTitle>
                        <ModalParagraph>
                            {t('settings:personal_information:enter_code_description', {
                                phone: maskPhoneNumber(`+${phoneNumberValue.replace('+', '')}`)
                            })}
                        </ModalParagraph>
                    </div>

                    <div className="flex flex-col gap-4">
                        <OtpInput
                            containerStyle={{display: 'flex', gap: 8}}
                            skipDefaultStyles={true}
                            value={otp}
                            onChange={value => setOtp(value)}
                            numInputs={4}
                            inputType={'number'}
                            renderInput={props => (
                                <input
                                    {...props}
                                    className="box-border h-16 w-16 rounded-lg border border-neutral-300 px-2 py-1 text-center text-display-md font-semibold text-neutral-900 disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-400 [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none [&[type=number]]:[-moz-appearance:textfield]"
                                    //chrome on ios didn't fire the onPaste from the keyboard
                                    onInput={event => setOtp((event.target as HTMLInputElement).value)}
                                    name="otp-code"
                                    id={'otp-code'}
                                    auto-complete="one-time-code"
                                    disabled={isPending}
                                />
                            )}
                        />
                        <div className="flex items-center gap-1 text-sm text-neutral-900">
                            {t('settings:personal_information:resend_code')}
                            <Button
                                variant="tertiary"
                                shape="text"
                                onClick={() => resendVerificationPhoneCodeMutation.mutate()}
                                disabled={resendVerificationPhoneCodeMutation.isPending}
                            >
                                {t('settings:personal_information:send_again')}
                            </Button>
                            {resendVerificationPhoneCodeMutation.isPending && <Spinner size={16} />}
                        </div>
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button variant="tertiary" onClick={onClose} disabled={isPending}>
                    {t('commons:cancel')}
                    {isPending && <Spinner />}
                </Button>
                <Button onClick={handleSubmit} disabled={isPending || otp.length != 4}>
                    {t('settings:personal_information:confirm_phone')}
                    {isPending && <Spinner />}
                </Button>
            </ModalFooter>
        </>
    )
}
